import React from 'react';
import { useUserLookup, isAuth0Id } from '../../hooks/useUserLookup';

interface UserNameProps {
  /** The Auth0 ID or user name to display */
  userId?: string | null;
  /** Fallback user name if userId is not provided */
  userName?: string | null;
  /** Text to show if no user is provided */
  fallback?: string;
}

/**
 * Component that displays a user's name, querying MongoDB if needed.
 *
 * Usage:
 * <UserName userId="auth0|bbbbbbbbbbbbbbbbbbbb0007" />
 * <UserName userId={order.packagedBy} fallback="Unassigned" />
 */
export function UserName({ userId, userName, fallback = 'Unknown' }: UserNameProps) {
  // Use userName if provided and it's not an Auth0 ID
  const valueToLookup = userId || userName;

  const { displayName, loading } = useUserLookup(valueToLookup);

  // If userName is provided and is NOT an Auth0 ID, use it directly
  if (userName && !isAuth0Id(userName)) {
    return <>{formatSystemName(userName)}</>;
  }

  // If no value provided, show fallback
  if (!valueToLookup) {
    return <>{fallback}</>;
  }

  // If not an Auth0 ID, format and return
  if (!isAuth0Id(valueToLookup)) {
    return <>{formatSystemName(valueToLookup)}</>;
  }

  // Show the looked-up name (or the ID while loading)
  return <>{displayName || fallback}</>;
}

/**
 * Format system names like "system" -> "System", "technician" -> "Technician"
 */
function formatSystemName(value: string): string {
  const systemValues = ['system', 'technician', 'pharmacist', 'admin', 'unknown'];
  if (systemValues.includes(value.toLowerCase())) {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
  return value;
}

export default UserName;
